import { useEffect, useRef } from 'react';
import { Editor, Monaco, OnMount, loader } from '@monaco-editor/react';
import { ReportBundleDiagnostic, ReportBundleFiles } from '@/reports/bundles/types';
import { ReportSdkTypesState } from '@/frontend/store/ReportBundleStore';

interface BundleCodeEditorProps {
  files: ReportBundleFiles;
  active: string;
  onChange: (path: string, content: string) => void;
  diagnostics: ReportBundleDiagnostic[];
  sdkTypes: ReportSdkTypesState | null;
  onSave: () => void;
  /** the file is shown but not edited: one the server writes */
  readOnly?: boolean;
  /** a line to bring into view and put the cursor on; a new object each time it is asked for */
  reveal?: { path: string; line: number } | null;
}

const BUNDLE_ROOT = 'file:///bundle/';
const MARKER_OWNER = 'veriform-bundle';

function languageFor(path: string): string {
  return path.endsWith('.json') ? 'json' : 'typescript';
}

/**
 * Monaco over the bundle's files: one model per file so imports between them
 * resolve, the SDK's sources and React's types as extra libs so the author
 * gets real type checking, and the server's compile diagnostics as markers.
 */
export function BundleCodeEditor({ files, active, onChange, diagnostics, sdkTypes, onSave, readOnly = false, reveal = null }: BundleCodeEditorProps) {
  const monacoRef = useRef<Monaco | null>(null);
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  // The Editor keeps the beforeMount it was first given, and Monaco arrives
  // from the CDN whenever it does, so both read the latest props through refs.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const sdkTypesRef = useRef(sdkTypes);
  sdkTypesRef.current = sdkTypes;
  const filesRef = useRef(files);
  filesRef.current = files;

  const beforeMount = (monaco: Monaco) => {
    monacoRef.current = monaco;
    configureTypeScript(monaco, sdkTypesRef.current);
    syncModels(monaco, filesRef.current);
  };

  const onMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => onSaveRef.current());
  };

  // the line asked for, once its file is the one showing
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !reveal || reveal.path !== active) return;
    editor.revealLineInCenter(reveal.line);
    editor.setPosition({ lineNumber: reveal.line, column: 1 });
    editor.focus();
  }, [reveal, active]);

  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco) syncModels(monaco, files);
  }, [files]);

  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco) applyMarkers(monaco, files, diagnostics);
  }, [diagnostics, files]);

  useEffect(() => {
    const monaco = monacoRef.current;
    if (monaco && sdkTypes) configureTypeScript(monaco, sdkTypes);
  }, [sdkTypes]);

  return (
    <Editor
      height="100%"
      path={`${BUNDLE_ROOT}${active}`}
      language={languageFor(active)}
      defaultLanguage={languageFor(active)}
      value={files[active] ?? ''}
      defaultValue={files[active] ?? ''}
      theme="vs-light"
      className="bundle-code-editor"
      beforeMount={beforeMount}
      onMount={onMount}
      onChange={(value) => onChange(active, value ?? '')}
      options={{
        minimap: { enabled: false },
        fontSize: 13,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        tabSize: 2,
        insertSpaces: true,
        renderWhitespace: 'selection',
        fixedOverflowWidgets: true,
        readOnly,
      }}
    />
  );
}

let configuredFor: ReportSdkTypesState | null = null;

/** What the TypeScript worker says about one file, as far as the check below reads it. */
interface WorkerDiagnostic {
  category: number;
  start?: number;
  messageText: string | { messageText: string; next?: WorkerDiagnostic['messageText'][] };
}

interface TypeScriptWorker {
  getSyntacticDiagnostics: (uri: string) => Promise<WorkerDiagnostic[]>;
  getSemanticDiagnostics: (uri: string) => Promise<WorkerDiagnostic[]>;
}

/** The slice of Monaco's TypeScript namespace the editor configures. */
interface MonacoTypeScript {
  getTypeScriptWorker: () => Promise<(...uris: unknown[]) => Promise<TypeScriptWorker>>;
  typescriptDefaults: {
    setCompilerOptions: (options: Record<string, unknown>) => void;
    setEagerModelSync: (eager: boolean) => void;
    setExtraLibs: (libs: Array<{ content: string; filePath?: string }>) => void;
  };
  ScriptTarget: Record<string, number>;
  ModuleKind: Record<string, number>;
  ModuleResolutionKind: Record<string, number>;
  JsxEmit: Record<string, number>;
}

/** monaco-editor 0.55 moved `languages.typescript` to a top-level `typescript`; the CDN build may be either side of that. */
function typescriptOf(monaco: Monaco): MonacoTypeScript | null {
  const m = monaco as unknown as { typescript?: MonacoTypeScript; languages: { typescript?: MonacoTypeScript } };
  return m.typescript ?? m.languages.typescript ?? null;
}

function configureTypeScript(monaco: Monaco, sdkTypes: ReportSdkTypesState | null) {
  const ts = typescriptOf(monaco);
  if (!ts) return;
  ts.typescriptDefaults.setCompilerOptions({
    target: ts.ScriptTarget.ES2020,
    module: ts.ModuleKind.ESNext,
    moduleResolution: ts.ModuleResolutionKind.NodeJs,
    jsx: ts.JsxEmit.ReactJSX,
    strict: true,
    esModuleInterop: true,
    allowNonTsExtensions: true,
    noEmit: true,
    baseUrl: 'file:///',
    paths: sdkTypes?.paths ?? {},
  });
  ts.typescriptDefaults.setEagerModelSync(true);
  if (sdkTypes && configuredFor !== sdkTypes) {
    configuredFor = sdkTypes;
    ts.typescriptDefaults.setExtraLibs(Object.entries(sdkTypes.files).map(([path, content]) => ({ content, filePath: `file:///${path}` })));
  }
}

/**
 * One model per bundle file; models for files no longer in the bundle are
 * disposed. A file changed from outside the editor — the assistant, a restored
 * version — is written into its model as an edit, so undo still reaches back
 * past it and the type checker sees every file as it stands.
 */
function syncModels(monaco: Monaco, files: ReportBundleFiles) {
  for (const [path, content] of Object.entries(files)) {
    const uri = monaco.Uri.parse(`${BUNDLE_ROOT}${path}`);
    const model = monaco.editor.getModel(uri);
    if (!model) monaco.editor.createModel(content, languageFor(path), uri);
    else if (model.getValue() !== content) model.pushEditOperations([], [{ range: model.getFullModelRange(), text: content }], () => null);
  }
  for (const model of monaco.editor.getModels()) {
    const uri = model.uri.toString();
    if (uri.startsWith(BUNDLE_ROOT) && !(uri.slice(BUNDLE_ROOT.length) in files)) model.dispose();
  }
}

function applyMarkers(monaco: Monaco, files: ReportBundleFiles, diagnostics: ReportBundleDiagnostic[]) {
  for (const path of Object.keys(files)) {
    const model = monaco.editor.getModel(monaco.Uri.parse(`${BUNDLE_ROOT}${path}`));
    if (!model) continue;
    const markers = diagnostics
      .filter((d) => d.file === path)
      .map((d) => ({
        severity: monaco.MarkerSeverity.Error,
        message: d.message,
        startLineNumber: d.line ?? 1,
        startColumn: (d.column ?? 0) + 1,
        endLineNumber: d.line ?? 1,
        endColumn: d.line ? model.getLineMaxColumn(Math.min(d.line, model.getLineCount())) : 1,
      }));
    monaco.editor.setModelMarkers(model, MARKER_OWNER, markers);
  }
}

const flatten = (text: WorkerDiagnostic['messageText']): string => (typeof text === 'string' ? text : [text.messageText, ...(text.next ?? []).map(flatten)].join(' '));

/**
 * The editor's own TypeScript check over the bundle's files: the errors the
 * author sees underlined, as diagnostics. Compiling only bundles, so this is
 * the one place a type error shows. Null while Monaco has not loaded.
 */
export async function checkBundleTypes(files: ReportBundleFiles): Promise<ReportBundleDiagnostic[] | null> {
  const monaco = (loader?.__getMonacoInstance?.() ?? null) as Monaco | null;
  const ts = monaco ? typescriptOf(monaco) : null;
  if (!monaco || !ts) return null;
  syncModels(monaco, files);
  const getWorker = await ts.getTypeScriptWorker();
  const diagnostics: ReportBundleDiagnostic[] = [];
  for (const path of Object.keys(files).filter((p) => /\.tsx?$/.test(p)).sort()) {
    const uri = monaco.Uri.parse(`${BUNDLE_ROOT}${path}`);
    const model = monaco.editor.getModel(uri);
    if (!model) continue;
    const worker = await getWorker(uri);
    const found = [...(await worker.getSyntacticDiagnostics(uri.toString())), ...(await worker.getSemanticDiagnostics(uri.toString()))];
    for (const d of found) {
      if (d.category !== 1) continue;
      const at = d.start === undefined ? null : model.getPositionAt(d.start);
      diagnostics.push({ file: path, line: at?.lineNumber ?? null, column: at ? at.column - 1 : null, message: flatten(d.messageText) });
    }
  }
  return diagnostics;
}
